import { IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { MapBalloon } from '../../../components/MapBallon';
import { MapBalloonWithRoute } from '../../../components/MapBallonWithRoute';
import {
  IInfrastructureData,
  ITAInfrastructureData,
  TInfrastructureItem,
  TTAInfrastructureItem,
  TTAInfrastructureType,
} from '../../../types/map';
import { useApplicationContext } from '../../ApplicationContext';
import { findGeoObjectDatum, getIsDistanceCalculatingEnabled } from '../utils';
import {
  getDateText,
  getDescription,
  getIconBackgroundColor,
  getTimeWalk,
  isRouteForTypeAvailable,
} from '../utils/balloon';

interface IUseGeoObjectBalloonParams {
  infrastructureByYearRef: React.MutableRefObject<Map<number, IInfrastructureData>>;
  transportAccessibilityInfrastructureByTypeRef: React.MutableRefObject<
    Map<TTAInfrastructureType, ITAInfrastructureData>
  >;
  onRouteButtonClick(item: TInfrastructureItem | TTAInfrastructureItem): void;
  resetTARouting: () => void;
}

export const useGeoObjectBalloon = ({
  infrastructureByYearRef,
  transportAccessibilityInfrastructureByTypeRef,
  onRouteButtonClick,
  resetTARouting,
}: IUseGeoObjectBalloonParams) => {
  const { config } = useApplicationContext();

  const isDistanceCalculatingEnabled = getIsDistanceCalculatingEnabled(config);

  const handleBalloonHtmlFirstFetch = React.useCallback(
    async (geoObject: IMapGeoObject): Promise<string> => {
      const {
        name: type,
        options: { id },
      } = geoObject;

      const datum = findGeoObjectDatum(
        id,
        infrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
      );
      const item = datum?.infrastructureItem;

      if (!item) {
        return Promise.reject(new Error('Не верный тип пина!'));
      }

      const balloonProps = {
        description: getDescription(item),
        routeButtonId: `route-button-${item.id}`,
        timeWalk: getTimeWalk(item),
        title: item.title,
        type,
        iconBackgroundColor: getIconBackgroundColor(item),
        dateText: getDateText(item),
      };

      const MapBallonComponent =
        isDistanceCalculatingEnabled && isRouteForTypeAvailable(item.type) ? MapBalloonWithRoute : MapBalloon;

      const markup = renderToStaticMarkup(<MapBallonComponent {...balloonProps} />);

      return Promise.resolve(markup);
    },
    [isDistanceCalculatingEnabled], // eslint-disable-line
  );

  const handleBalloonOpen = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const datum = findGeoObjectDatum(
        geoObject.options.id,
        infrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
      );
      const item = datum?.infrastructureItem as TInfrastructureItem | TTAInfrastructureItem;

      const routeButton = document.getElementById(`route-button-${item.id}`);

      if (routeButton) {
        routeButton.addEventListener('click', () => {
          onRouteButtonClick(item);
        });
      }

      resetTARouting();
    },
    [onRouteButtonClick, resetTARouting], // eslint-disable-line
  );

  return { onBalloonHtmlFirstFetch: handleBalloonHtmlFirstFetch, onBalloonOpen: handleBalloonOpen };
};
